import React, { useState, useEffect } from 'react';
import { X, Settings, Trash2 } from 'lucide-react';
import SportsbookLogo from './SportsbookLogo';

const ALL_BOOKS = [
  { key: 'draftkings', name: 'DraftKings' },
  { key: 'fanduel', name: 'FanDuel' },
  { key: 'betmgm', name: 'BetMGM' },
  { key: 'williamhill_us', name: 'Caesars' },
  { key: 'fanatics', name: 'Fanatics' },
  { key: 'espnbet', name: 'theScore Bet' },
  { key: 'ballybet', name: 'Bally Bet' },
  { key: 'betrivers', name: 'BetRivers' },
];

function SettingsModal({ isOpen, onClose, selectedBooks, bankroll: bankrollProp, kellyFraction: kellyFractionProp, arbAlertEnabled: arbAlertEnabledProp, arbAlertThreshold: arbAlertThresholdProp, arbAlertAllBooks: arbAlertAllBooksProp, onSave, session, onDeleteAccount }) {
  const [localSelected, setLocalSelected] = useState(
    selectedBooks.length > 0 ? selectedBooks : ALL_BOOKS.map(b => b.key)
  );
  const [bankroll, setBankroll] = useState(bankrollProp != null ? String(bankrollProp) : '');
  const [kellyFraction, setKellyFraction] = useState(kellyFractionProp != null ? String(kellyFractionProp) : '0.25');
  const [arbAlertEnabled, setArbAlertEnabled] = useState(!!arbAlertEnabledProp);
  const [arbAlertThreshold, setArbAlertThreshold] = useState(
    arbAlertThresholdProp != null ? String(arbAlertThresholdProp) : '1.0'
  );
  const [arbAlertAllBooks, setArbAlertAllBooks] = useState(!!arbAlertAllBooksProp);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setLocalSelected(selectedBooks.length > 0 ? selectedBooks : ALL_BOOKS.map(b => b.key));
      setBankroll(bankrollProp != null ? String(bankrollProp) : '');
      setKellyFraction(kellyFractionProp != null ? String(kellyFractionProp) : '0.25');
      setArbAlertEnabled(!!arbAlertEnabledProp);
      setArbAlertThreshold(arbAlertThresholdProp != null ? String(arbAlertThresholdProp) : '1.0');
      setArbAlertAllBooks(!!arbAlertAllBooksProp);
      setShowDeleteConfirm(false);
      setDeleteError('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const handleToggle = (bookKey) => {
    setLocalSelected(prev =>
      prev.includes(bookKey)
        ? prev.filter(k => k !== bookKey)
        : [...prev, bookKey]
    );
  };

  const handleSelectAll = () => {
    setLocalSelected(ALL_BOOKS.map(b => b.key));
  };

  const handleSelectNone = () => {
    setLocalSelected([]);
  };

  const handleSave = () => {
    onSave({
      books: localSelected,
      bankroll,
      kellyFraction,
      arbAlertEnabled,
      arbAlertThreshold: parseFloat(arbAlertThreshold) || 1.0,
      arbAlertAllBooks,
    });
    onClose();
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    setDeleteError('');
    try {
      await onDeleteAccount();
    } catch (err) {
      setDeleteError('Something went wrong. Please try again.');
      setDeleteLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <Settings size={20} />
            Settings
          </h2>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-content">
          <h3>My Sportsbooks</h3>
          <p className="modal-description">
            Select the sportsbooks you have accounts with. Only odds from selected books will be shown.
          </p>

          <div className="select-actions">
            <button onClick={handleSelectAll} className="text-btn">Select All</button>
            <button onClick={handleSelectNone} className="text-btn">Select None</button>
          </div>

          <div className="book-checkboxes">
            {ALL_BOOKS.map(book => (
              <label key={book.key} className="book-checkbox">
                <input
                  type="checkbox"
                  checked={localSelected.includes(book.key)}
                  onChange={() => handleToggle(book.key)}
                />
                <span className="checkmark"></span>
                <SportsbookLogo bookKey={book.key} size={18} />
                {book.name}
              </label>
            ))}
          </div>

          {localSelected.length === 0 && (
            <p className="warning-text">Please select at least one sportsbook</p>
          )}

          <hr className="settings-divider" />

          <h3>Bankroll & Bet Sizing</h3>
          <p className="modal-description">
            Enter your total betting bankroll to get suggested bet sizes on +EV opportunities.
            Bet sizes are calculated using the <strong>Kelly Criterion</strong> — a formula that
            tells you the mathematically optimal percentage of your bankroll to wager based on your
            edge. Betting too little leaves money on the table; betting too much risks ruin.
          </p>

          <div className="settings-field">
            <label className="settings-label">
              Bankroll ($):
              <input
                type="number"
                className="settings-input"
                value={bankroll}
                onChange={(e) => setBankroll(e.target.value)}
                placeholder="e.g. 1000"
                min="0"
              />
            </label>
          </div>

          <div className="settings-field">
            <label className="settings-label">
              Kelly Fraction:
              <select
                className="settings-select"
                value={kellyFraction}
                onChange={(e) => setKellyFraction(e.target.value)}
              >
                <option value="1.0">Full Kelly — max growth, high variance (aggressive)</option>
                <option value="0.5">Half Kelly — smoother growth, moderate risk</option>
                <option value="0.25">Quarter Kelly — recommended for most bettors</option>
                <option value="0.1">Tenth Kelly — very small bets, minimal risk</option>
              </select>
            </label>
          </div>
          <p className="modal-description modal-description--hint">
            Most bettors use <strong>Quarter Kelly</strong> — it captures most of the mathematical
            edge while keeping individual bet sizes small enough to survive a losing streak.
            Full Kelly maximizes long-run growth but swings are large.
          </p>

          {session && (
            <>
              <hr className="settings-divider" />
              <h3>Arbitrage Email Alerts</h3>
              <p className="modal-description">
                Get an email when a new arbitrage opportunity meets your profit threshold.
                Alerts are sent to <strong>{session.user?.email}</strong> and only cover upcoming games (not live).
              </p>

              <div className="settings-field settings-field--toggle">
                <label className="settings-label settings-label--toggle">
                  <span>Enable arb alerts</span>
                  <button
                    className={`toggle-btn${arbAlertEnabled ? ' toggle-btn--on' : ''}`}
                    onClick={() => setArbAlertEnabled(v => !v)}
                    aria-pressed={arbAlertEnabled}
                    type="button"
                  >
                    <span className="toggle-thumb" />
                  </button>
                </label>
              </div>

              {arbAlertEnabled && (
                <>
                  <div className="settings-field">
                    <label className="settings-label">
                      Minimum profit %:
                      <input
                        type="number"
                        className="settings-input"
                        value={arbAlertThreshold}
                        onChange={e => setArbAlertThreshold(e.target.value)}
                        min="0.1"
                        step="0.1"
                        placeholder="e.g. 1.0"
                      />
                    </label>
                    <p className="modal-description modal-description--hint">
                      Only opportunities at or above this profit % will trigger an email.
                    </p>
                  </div>
                  <div className="settings-field">
                    <label className="settings-label settings-label--checkbox">
                      <input
                        type="checkbox"
                        checked={arbAlertAllBooks}
                        onChange={e => setArbAlertAllBooks(e.target.checked)}
                      />
                      <span>Include opportunities from all books (not just my selection)</span>
                    </label>
                  </div>
                </>
              )}
            </>
          )}

          {session && (
            <>
              <hr className="settings-divider" />
              <div className="danger-zone">
                <h3 className="danger-zone-title">Delete Account</h3>
                {!showDeleteConfirm ? (
                  <>
                    <p className="modal-description">
                      Your account and all saved promos will be removed. Bet history cannot be recovered.
                    </p>
                    <button
                      className="btn-danger-outline"
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      <Trash2 size={15} />
                      Delete Account
                    </button>
                  </>
                ) : (
                  <div className="delete-confirm-box">
                    <p className="delete-confirm-text">
                      Are you sure? This will permanently delete your account and all your data.
                    </p>
                    {deleteError && <p className="warning-text">{deleteError}</p>}
                    <div className="delete-confirm-actions">
                      <button
                        className="btn-secondary"
                        onClick={() => { setShowDeleteConfirm(false); setDeleteError(''); }}
                        disabled={deleteLoading}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn-danger"
                        onClick={handleDeleteConfirm}
                        disabled={deleteLoading}
                      >
                        {deleteLoading ? 'Deleting...' : 'Yes, delete my account'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={localSelected.length === 0}
          >
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
}

export default SettingsModal;
